// qs-useZoomPerformance.ts
import { computed, ref } from 'vue'
import { usePerformanceMonitor } from '@/composables/qs-usePerformanceMonitor'
import type { KonvaStage } from '@/types/qs-type-konva'

interface ZoomQualityLevel {
  threshold: number
  quality: number
  description: string
}

interface ZoomOptions {
  forceImmediate?: boolean
  enableQualityScaling?: boolean
  enableThrottling?: boolean
}

const FPS_THRESHOLD_HIGH = 55
const FPS_THRESHOLD_MEDIUM = 30
const FRAME_TIME_THRESHOLD_FAST = 16.67
const FRAME_TIME_THRESHOLD_SLOW = 33.33

const ZOOM_QUALITY_LEVELS: Record<string, ZoomQualityLevel> = {
  HIGH: { threshold: 2.0, quality: 1.0, description: 'Full Quality' },
  MEDIUM: { threshold: 4.0, quality: 0.8, description: 'Medium Quality' },
  LOW: { threshold: 8.0, quality: 0.6, description: 'Low Quality' },
  MINIMAL: { threshold: Infinity, quality: 0.4, description: 'Minimal Quality' }
}

export function useZoomPerformance() {
  const performanceMonitor = usePerformanceMonitor()

  const isZooming = ref(false)
  const zoomQueue = ref<Array<() => void>>([])
  const lastZoomTime = ref(0)
  const zoomThrottleMs = ref(16)
  const adaptiveThrottleEnabled = ref(true)
  const idleCallbackEnabled = ref(true)
  const qualityScalingEnabled = ref(true)

  const currentFps = computed(() => performanceMonitor.fps?.value || 60)
  const currentFrameTime = computed(() => performanceMonitor.frameTime?.value || 16.67)
  const currentMemoryUsage = computed(() => performanceMonitor.memoryUsage?.value || 0)

  const calculateAdaptiveThrottle = (): number => {
    if (!adaptiveThrottleEnabled.value) return 16

    const fps = currentFps.value
    const frameTime = currentFrameTime.value

    if (fps >= FPS_THRESHOLD_HIGH && frameTime <= FRAME_TIME_THRESHOLD_FAST) {
      return 8
    }

    if (fps >= FPS_THRESHOLD_MEDIUM && frameTime <= FRAME_TIME_THRESHOLD_SLOW) {
      return 16
    }

    if (fps < FPS_THRESHOLD_MEDIUM || frameTime > FRAME_TIME_THRESHOLD_SLOW) {
      return 33
    }

    return 16
  }

  const updateAdaptiveThrottle = () => {
    const newThrottle = calculateAdaptiveThrottle()
    zoomThrottleMs.value = newThrottle

    window.dispatchEvent(new CustomEvent('zoom-performance-update', {
      detail: {
        throttle: newThrottle,
        fps: currentFps.value,
        frameTime: currentFrameTime.value
      }
    }))
  }

  const shouldThrottleZoom = (): boolean => {
    const now = performance.now()
    return (now - lastZoomTime.value) < zoomThrottleMs.value
  }

  const getZoomQualityLevel = (scale: number): ZoomQualityLevel => {
    if (!qualityScalingEnabled.value) return ZOOM_QUALITY_LEVELS.HIGH

    for (const level of Object.values(ZOOM_QUALITY_LEVELS)) {
      if (scale <= level.threshold) {
        return level
      }
    }
    return ZOOM_QUALITY_LEVELS.MINIMAL
  }

  const applyQualityScaling = (stage: KonvaStage, scale: number) => {
    if (!qualityScalingEnabled.value) return

    const qualityLevel = getZoomQualityLevel(scale)

    if (stage && 'scale' in stage) {
      if ('pixelRatio' in stage) {
        (stage as { pixelRatio: number }).pixelRatio = qualityLevel.quality
      }

      if (qualityLevel.quality < 0.8 && 'listening' in stage) {
        (stage as { listening: (enabled: boolean) => void }).listening(false)
      } else if ('listening' in stage) {
        (stage as { listening: (enabled: boolean) => void }).listening(true)
      }
    }
  }

  const performZoom = (zoomFunction: () => void, forceImmediate = false) => {
    if (forceImmediate) {
      zoomFunction()
      lastZoomTime.value = performance.now()
      return
    }

    if (shouldThrottleZoom()) {
      zoomQueue.value.push(zoomFunction)
      return
    }

    zoomFunction()
    lastZoomTime.value = performance.now()
  }

  const processZoomQueue = () => {
    if (zoomQueue.value.length === 0) return

    const processNext = () => {
      if (zoomQueue.value.length > 0) {
        const nextZoom = zoomQueue.value.shift()
        if (nextZoom) {
          nextZoom()
          lastZoomTime.value = performance.now()
        }
      }
    }

    if (idleCallbackEnabled.value && 'requestIdleCallback' in window) {
      const requestIdleCallback = (window as { requestIdleCallback?: typeof window.requestIdleCallback }).requestIdleCallback
      if (requestIdleCallback) {
        requestIdleCallback(processNext, { timeout: 100 })
      } else {
        setTimeout(processNext, 0)
      }
    } else {
      setTimeout(processNext, 0)
    }
  }

  const optimizedZoom = (
    zoomFunction: () => void,
    stage: KonvaStage | null,
    scale: number,
    options: ZoomOptions = {}
  ) => {
    const {
      forceImmediate = false,
      enableQualityScaling = true,
      enableThrottling = true
    } = options

    if (enableQualityScaling && stage) {
      applyQualityScaling(stage, scale)
    }

    if (enableThrottling) {
      performZoom(zoomFunction, forceImmediate)
    } else {
      zoomFunction()
      lastZoomTime.value = performance.now()
    }

    processZoomQueue()
  }

  const startPerformanceMonitoring = () => {
    const performanceInterval = setInterval(() => {
      updateAdaptiveThrottle()
    }, 500)

    const queueInterval = setInterval(() => {
      processZoomQueue()
    }, 100)

    return () => {
      clearInterval(performanceInterval)
      clearInterval(queueInterval)
    }
  }

  const setAdaptiveThrottling = (enabled: boolean) => {
    adaptiveThrottleEnabled.value = enabled
    if (!enabled) {
      zoomThrottleMs.value = 16
    }
  }

  const setIdleCallback = (enabled: boolean) => {
    idleCallbackEnabled.value = enabled
  }

  const setQualityScaling = (enabled: boolean) => {
    qualityScalingEnabled.value = enabled
  }

  const getPerformanceStatus = () => ({
    isZooming: isZooming.value,
    currentThrottle: zoomThrottleMs.value,
    queueLength: zoomQueue.value.length,
    fps: currentFps.value,
    frameTime: currentFrameTime.value,
    memoryUsage: currentMemoryUsage.value,
    adaptiveThrottling: adaptiveThrottleEnabled.value,
    idleCallback: idleCallbackEnabled.value,
    qualityScaling: qualityScalingEnabled.value
  })

  const cleanup = () => {
    zoomQueue.value = []
    isZooming.value = false
  }

  let cleanupMonitoring: (() => void) | null = null

  const startPerformanceMonitoringManually = () => {
    if (cleanupMonitoring) {
      cleanupMonitoring()
      cleanupMonitoring = null
    }
    cleanupMonitoring = startPerformanceMonitoring()
  }

  const stopPerformanceMonitoringManually = () => {
    if (cleanupMonitoring) {
      cleanupMonitoring()
      cleanupMonitoring = null
    }
    cleanup()
  }

  return {
    isZooming,
    zoomThrottleMs,
    adaptiveThrottleEnabled,
    idleCallbackEnabled,
    qualityScalingEnabled,
    currentFps,
    currentFrameTime,
    currentMemoryUsage,
    optimizedZoom,
    performZoom,
    applyQualityScaling,
    getZoomQualityLevel,
    getPerformanceStatus,
    setAdaptiveThrottling,
    setIdleCallback,
    setQualityScaling,
    cleanup,
    startPerformanceMonitoringManually,
    stopPerformanceMonitoringManually
  }
}